import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const [personalization, setPersonalization] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem('fbs-cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('fbs-cookie-consent', 'all');
    setVisible(false);
  };

  const handleAcceptNecessary = () => {
    localStorage.setItem('fbs-cookie-consent', 'necessary');
    setVisible(false);
  };

  const handleSaveSettings = () => {
    const settings = { analytics, marketing, personalization };
    localStorage.setItem('fbs-cookie-consent', JSON.stringify(settings));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] animate-slide-up px-3 sm:px-4 pb-3 sm:pb-4">
      <div className="bg-[#161616] border border-[#2a2a2a] rounded-2xl shadow-2xl shadow-black/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">

          {/* Main banner */}
          {!showSettings && (
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex-1">
                <h4 className="text-white font-semibold text-sm sm:text-base mb-1">
                  {t('cookie.title')}
                </h4>
                <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                  {t('cookie.description')}{' '}
                  <a href="/ochrana-udajov" className="text-accent-green hover:underline">{t('cookie.privacyLink')}</a>,{' '}
                  <a href="/cookies" className="text-accent-green hover:underline">{t('cookie.cookiesLink')}</a>{' '}
                  {t('cookie.and')}{' '}
                  <a href="/gdpr" className="text-accent-green hover:underline">GDPR</a>.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto flex-shrink-0">
                <button
                  onClick={handleAcceptNecessary}
                  className="px-5 py-2.5 rounded-full border border-[#2a2a2a] text-gray-300 text-sm font-medium hover:border-accent-green hover:text-accent-green transition-colors whitespace-nowrap"
                >
                  {t('cookie.necessaryOnly')}
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-5 py-2.5 rounded-full border border-[#2a2a2a] text-gray-300 text-sm font-medium hover:border-accent-green hover:text-accent-green transition-colors whitespace-nowrap"
                >
                  {t('cookie.personalize')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 rounded-full bg-accent-green hover:bg-[#d4ff33] text-black text-sm font-semibold transition-colors whitespace-nowrap"
                >
                  {t('cookie.acceptAll')}
                </button>
              </div>
            </div>
          )}

          {/* Settings panel */}
          {showSettings && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-semibold text-sm sm:text-base">
                  {t('cookie.settingsTitle')}
                </h4>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  {t('cookie.back')}
                </button>
              </div>

              <div className="space-y-3">
                {/* Necessary - always on */}
                <div className="flex items-center justify-between py-2 border-b border-[#2a2a2a]">
                  <div>
                    <span className="text-white text-sm font-medium">{t('cookie.necessary')}</span>
                    <p className="text-gray-500 text-xs">{t('cookie.necessaryDesc')}</p>
                  </div>
                  <div className="w-10 h-5 bg-accent-green rounded-full opacity-60 cursor-not-allowed flex items-center justify-end px-0.5">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between py-2 border-b border-[#2a2a2a]">
                  <div>
                    <span className="text-white text-sm font-medium">{t('cookie.analytics')}</span>
                    <p className="text-gray-500 text-xs">{t('cookie.analyticsDesc')}</p>
                  </div>
                  <button
                    onClick={() => setAnalytics(!analytics)}
                    className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${analytics ? 'bg-accent-green justify-end' : 'bg-gray-600 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between py-2 border-b border-[#2a2a2a]">
                  <div>
                    <span className="text-white text-sm font-medium">{t('cookie.marketing')}</span>
                    <p className="text-gray-500 text-xs">{t('cookie.marketingDesc')}</p>
                  </div>
                  <button
                    onClick={() => setMarketing(!marketing)}
                    className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${marketing ? 'bg-accent-green justify-end' : 'bg-gray-600 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>

                {/* Personalization */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <span className="text-white text-sm font-medium">{t('cookie.personalization')}</span>
                    <p className="text-gray-500 text-xs">{t('cookie.personalizationDesc')}</p>
                  </div>
                  <button
                    onClick={() => setPersonalization(!personalization)}
                    className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${personalization ? 'bg-accent-green justify-end' : 'bg-gray-600 justify-start'}`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleSaveSettings}
                  className="px-5 py-2.5 rounded-full bg-accent-green hover:bg-[#d4ff33] text-black text-sm font-semibold transition-colors whitespace-nowrap"
                >
                  {t('cookie.saveSettings')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 rounded-full border border-[#2a2a2a] text-gray-300 text-sm font-medium hover:border-accent-green hover:text-accent-green transition-colors whitespace-nowrap"
                >
                  {t('cookie.acceptAll')}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
